import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useOrders } from '../hooks/useOrders';
import { Order, OrderStatus } from '../types/order';
import CommonPageHeader from '../components/common/CommonPageHeader';
import SearchBar from '../components/common/SearchBar';
import OrderList from '../components/orders/OrderList';
import OrderForm from '../components/orders/OrderForm';
import OrderView from '../components/orders/OrderView';
import LoadingScreen from '../components/common/LoadingScreen';
import { 
  PlusIcon, 
  ShoppingCartIcon,
  FunnelIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const Orders: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { hasAccess } = usePermissions();
  const {
    orders,
    loading,
    updateOrderStatus,
    deleteOrder,
    updateOrder
  } = useOrders();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | 'all'>('all');
  
  // Modal states
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  // Jogosultság ellenőrzés
  if (!hasAccess('quotations')) { // Orders is part of quotations module
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('common.errors.loadingFailed')}</div>
      </div>
    );
  }

  // Szűrés
  const filteredOrders = orders.filter(order => {
    const matchesSearch = !searchTerm || 
      order.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = selectedStatus === 'all' || order.status === selectedStatus;
    
    return matchesSearch && matchesStatus;
  });

  const handleStatusChange = async (orderId: string, status: OrderStatus) => {
    const success = await updateOrderStatus(orderId, status);
    if (success) {
      toast.success(t('orders.messages.statusUpdated'));
    }
  };

  const handleEditClick = (order: Order) => {
    setSelectedOrder(order);
    setIsEditOpen(true);
  };

  const handleDeleteClick = (order: Order) => {
    if (window.confirm(t('orders.messages.confirmDelete'))) {
      deleteOrder(order.id);
    }
  };

  const handleViewClick = (order: Order) => {
    setSelectedOrder(order);
    setIsViewOpen(true);
  };

  const handleSaveOrder = async (orderId: string, updatedData: Partial<Order>) => {
    const success = await updateOrder(orderId, updatedData);
    if (success) {
      toast.success(t('orders.messages.orderUpdated'));
      setIsEditOpen(false);
      setSelectedOrder(null);
    } else {
      toast.error(t('orders.errors.updateFailed'));
    }
  };

  const handleCloseView = () => {
    setIsViewOpen(false);
    setSelectedOrder(null);
  };

  const handleCloseEdit = () => {
    setIsEditOpen(false);
    setSelectedOrder(null);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      <CommonPageHeader
        title={t('orders.title')}
        subtitle={t('orders.subtitle')}
        icon={ShoppingCartIcon}
      >
        <button
          onClick={() => toast(t('orders.list.createNewSoon'), { icon: '🚧' })}
          className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          <span>{t('orders.list.createNew')}</span>
        </button>
      </CommonPageHeader>

      {/* Keresés és szűrés */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Keresés */}
          <div className="flex-1">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={t('orders.list.searchPlaceholder')}
            />
          </div>

          {/* Státusz szűrő */}
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as OrderStatus | 'all')}
              className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            >
              <option value="all">{t('orders.filter.all')}</option>
              <option value="pending">{t('orders.filter.pending')}</option>
              <option value="confirmed">{t('orders.filter.confirmed')}</option>
              <option value="processing">{t('orders.filter.processing')}</option>
              <option value="shipped">{t('orders.filter.shipped')}</option>
              <option value="delivered">{t('orders.filter.delivered')}</option>
              <option value="cancelled">{t('orders.filter.cancelled')}</option>
              <option value="completed">{t('orders.filter.completed')}</option>
            </select>
          </div>

          {/* Frissítés gomb */}
          <button
            onClick={() => window.location.reload()}
            className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            {t('common.actionsObject.refresh')}
          </button>
        </div>

        {/* Statisztikák */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {filteredOrders.length}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">{t('orders.stats.total')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {filteredOrders.filter(o => o.status === 'pending').length}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">{t('orders.stats.pending')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {filteredOrders.filter(o => o.status === 'confirmed').length}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">{t('orders.stats.confirmed')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {filteredOrders.filter(o => o.status === 'delivered').length}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">{t('orders.stats.completed')}</div>
          </div>
        </div>
      </div>

      {/* Megrendelések lista */}
      <OrderList
        orders={filteredOrders}
        onEditClick={handleEditClick}
        onDeleteClick={handleDeleteClick}
        onViewClick={handleViewClick}
        onStatusChange={handleStatusChange}
        loading={loading}
      />

      {/* Megrendelés megtekintése modal */}
      <OrderView
        order={selectedOrder}
        isOpen={isViewOpen}
        onClose={handleCloseView}
      />

      {/* Megrendelés szerkesztése modal */}
      <OrderForm
        order={selectedOrder}
        isOpen={isEditOpen}
        onClose={handleCloseEdit}
        onSave={handleSaveOrder}
      />
    </div>
  );
};

export default Orders; 